import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost } from './client';

export interface Permission {
  id: string;
  permission: string;
  description: string;
}

export interface RoleSummary {
  id: string;
  organisation_id: string;
  name: string;
  description: string | null;
  is_default: boolean;
  permissions: Permission[];
}

export interface RoleDetail extends RoleSummary {
  member_count: number;
}

export interface CreateRoleInput {
  organisation_id: string;
  name: string;
  description?: string;
  is_default?: boolean;
  permission_ids?: string[];
}

export interface UpdateRoleInput {
  name?: string;
  description?: string;
  is_default?: boolean;
  permission_ids?: string[];
}

const KEYS = {
  // `orgId` may be empty — that's the "all orgs" view the Roles
  // landing page uses by default. Distinct cache key per filter so
  // switching from "All" to a specific org doesn't return stale data.
  list: (orgId: string) => ['roles', 'list', orgId || 'all'] as const,
  detail: (id: string) => ['roles', 'detail', id] as const,
};

export function useRoles(orgId: string | undefined) {
  return useQuery({
    queryKey: KEYS.list(orgId ?? ''),
    queryFn: () =>
      apiGet<RoleSummary[]>(orgId ? `/api/roles?org=${encodeURIComponent(orgId)}` : '/api/roles'),
  });
}

export function useRole(id: string | undefined) {
  return useQuery({
    queryKey: id ? KEYS.detail(id) : ['roles', 'detail', 'idle'],
    queryFn: () => apiGet<RoleDetail>(`/api/roles/${id}`),
    enabled: !!id,
  });
}

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRoleInput) => apiPost<RoleDetail>('/api/roles', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roles'] }),
  });
}

export function useUpdateRole(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateRoleInput) => apiPatch<RoleDetail>(`/api/roles/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roles'] }),
  });
}

export function useDeleteRole(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiDelete(`/api/roles/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roles'] }),
  });
}
